import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { storeAccessUserPlant } from "../../Store/TemplateStore/template.action";
import { API_URLS } from "../../network/appUrls";
import { UserExcelStrings } from "../../utils/constantsString";
import {
  getAllUsers,
  exportExcel,
  getAllPlantsForUser,
} from "../../network/cipAPI";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AgTable from "../../components/AgTable";
import DupontIconButton from "../../components/DupontIconButton";
import DupontButton from "../../components/DupontButton";
import { OverlayLoader } from "../../components/PageLoading";
import {
  formatToLocalDateTime,
  updatePlantId,
  getRequestStatusText,
  emptyValueFormatter,
} from "../../utils/Utils";
import { dateFormatter } from "../../utils/tableDataFormatter";
import PlantsRender from "../../components/AddCIPAndShutdown/plantsRender";
import ViewPlantCellRenderer from "../../components/AddCIPAndShutdown/ViewPlantCellRenderer";
import DupontDialog from "../../components/DupontDialog";
import { ViewTypeConstants } from "../../constants/global.constants";
import RequestStatusFilterHeader from "./customFilter";
import Footer from "../../common/footer/Footer";
import Header from "../../components/Header";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";

import "./index.scss";

const UserPage = () => {
  const { token } = useSelector((state) => state);
  // Row click handler for plant dialog table
  const handlePlantRowClicked = (event) => {
    const { data } = event;
    if (data.view === ViewTypeConstants.VIEW_EDIT_PLANT) {
      updatePlantId(data.plant_id);
      dispatch(storeAccessUserPlant(true));
      const path = `${API_URLS.basePath}editPlant?plantId=${data.plant_id}&isEdit=true`;
      navigate(path, { state: { noOfTrains: data.num_trains } });
    } else if (data.view === ViewTypeConstants.VIEW_ANALYSE_DATA) {
      updatePlantId(data.plant_id);
      dispatch(storeAccessUserPlant(true));
      const path = `${API_URLS.basePath}analyseData?plantId=${data.plant_id}&isEdit=true`;
      navigate(path);
    } else if (data.view === ViewTypeConstants.VIEW_HOME) {
      updatePlantId(data.plant_id);
      dispatch(storeAccessUserPlant(true));
      const path = `${API_URLS.basePath}home?plantId=${data.plant_id}`;
      navigate(path);
    }
  };
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showOverLayLoader, setShowOverLayLoader] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [plantRowData, setPlantRowData] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  // Handler to open dialog from PlantsRender
  const handleShowPlants = async ({ data }) => {
    setDialogOpen(true);
    setShowOverLayLoader(true);
    try {
      let plants = [];
      if (data && data.email) {
        const response = await getAllPlantsForUser(data.email);
        if (response && response.data) {
          plants = response.data;
        }
      }
      setPlantRowData(plants);
      // setDialogOpen(true);
    } catch (err) {
      setPlantRowData([]);
      setDialogOpen(true);
    } finally {
      setShowOverLayLoader(false);
    }
  };

  // Plant table column definitions for dialog
  const plantColDefs = [
    {
      headerName: t("ROOperationAdvisorTableStrings.name"),
      field: "name",
      sortable: true,
      flex: 1,
      wrapText: true,
      minWidth: 200,
    },

    {
      headerName: t("ROOperationAdvisorTableStrings.city"),
      field: "city",
      sortable: true,
      flex: 1,
      minWidth: 175,
    },
    {
      headerName: t("ROOperationAdvisorTableStrings.country"),
      field: "country",
      sortable: true,
      flex: 1,
      wrapText: true,
      minWidth: 200,
    },
    {
      headerName: t("ROOperationAdvisorTableStrings.createdon"),
      field: "created_at",
      valueFormatter: dateFormatter,
      sortable: true,
      flex: 1,
      minWidth: 225,
      sort: "desc",
    },
    {
      colId: "viewPlant",
      lockPinned: true,
      headerName: "View Plant",
      field: "viewPlant",
      pinned: "right",
      minWidth: 150,
      cellRenderer: ViewPlantCellRenderer,
    },
  ];
  const handleDialogClose = () => {
    setDialogOpen(false);
    // setDialogRowData(null);
    setPlantRowData([]);
  };

  useEffect(() => {
    const fetchAccessRequests = async () => {
      setShowOverLayLoader(true);
      try {
        const response = await getAllUsers(
          currentPage,
          pageSize,
          currentFilters,
          sortField,
          sortOrder,
        );

        if (response && response.data) {
          const total = response.data.total_pages || 1;
          const users = response.data.users || [];

          setRowData(users);
          setTotalPages(total);
        } else {
          setRowData([]);
          setTotalPages(1);
        }
      } catch (error) {
        setRowData([]);
        setTotalPages(1);
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          "",
        );
      } finally {
        setShowOverLayLoader(false);
      }
    };

    if (token) fetchAccessRequests();
  }, [currentPage, pageSize, currentFilters, sortField, sortOrder, token]);

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const response = await exportExcel(UserExcelStrings.exportType);
      if (response && response.data) {
        let fileName = t("adminPage.userFileName");
        if (response.headers && response.headers["content-disposition"]) {
          const match = response.headers["content-disposition"].match(
            /filename="?([^";]+)"?/,
          );
          if (match && match[1]) fileName = match[1];
        }
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setExportLoading(false);
    }
  };

  const onSortChanged = (event) => {
    const sortModel = event.columnApi
      .getAllColumns()
      .map((column) => {
        return {
          colId: column.getColId(),
          sort: column.getSort(),
        };
      })
      .filter((col) => col.sort);

    if (sortModel && sortModel.length > 0) {
      const { colId, sort } = sortModel[0];
      setSortField(colId);
      setSortOrder(sort);
    } else {
      setSortField("created_at");
      setSortOrder("desc");
    }
  };

  const onRequestStatusFilterChange = (filterQuery) => {
    const filters = filterQuery ? filterQuery.split(",") : [];
    setCurrentFilters(filters);
  };

  const colDefs = [
    {
      headerName: t("adminPage.userid"),
      field: "id",
      sortable: false,
      flex: 1,
      minWidth: 80,
    },
    {
      headerName: t("adminPage.userName"),
      field: "username",
      sortable: false,
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => emptyValueFormatter(params),
    },
    {
      headerName: t("adminPage.country"),
      field: "country",
      sortable: false,
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) => emptyValueFormatter(params),
    },
    {
      headerName: t("adminPage.company"),
      field: "company",
      sortable: false,
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) => emptyValueFormatter(params),
    },
    {
      headerName: t("adminPage.email"),
      field: "email",
      sortable: false,
      flex: 2.5,
      minWidth: 250,
    },
    {
      headerName: t("adminPage.created_at"),
      field: "created_at",
      sortable: true,
      valueFormatter: (params) => {
        return formatToLocalDateTime(params.value);
      },
      flex: 1.5,
      minWidth: 180,
    },
    {
      headerName: t("adminPage.role"),
      field: "user_role",
      sortable: false,
      flex: 1,
      minWidth: 100,
    },
    {
      headerName: t("adminPage.lastLogin"),
      field: "last_login",
      sortable: false,
      valueFormatter: (params) => {
        return formatToLocalDateTime(params.value);
      },
      flex: 1.5,
      minWidth: 180,
    },
    {
      headerName: t("adminPage.requestStatus"),
      field: "request_status",
      sortable: false,
      flex: 1.5,
      headerComponent: RequestStatusFilterHeader,
      headerComponentParams: {
        onFilterChange: onRequestStatusFilterChange,
        currentFilters: currentFilters,
      },
      valueGetter: (params) =>
        getRequestStatusText(t, params.data.request_status),
      minWidth: 160,
    },
    {
      headerName: t("adminPage.plantCount"),
      field: "plant_count",
      sortable: false,
      flex: 1.5,
      minWidth: 120,
    },
    {
      colId: "referenceCondition",
      lockPinned: true,
      pinned: "right",
      cellRenderer: PlantsRender,
      flex: 1,
      minWidth: 150,
    },
  ];

  return (
    <div className="container-main">
      {showOverLayLoader && <OverlayLoader customHeight={600} />}
      <Header />

      <div className="main-container">
        <div className="content-container">
          <div className="admin-title">
            <span>{t("adminPage.userDetails")}</span>
            <DupontButton onClick={handleExportExcel} disabled={exportLoading}>
              {exportLoading
                ? t("adminPage.exporting")
                : t("adminPage.exportExcel")}
            </DupontButton>
          </div>
          <div className="descriptionText marginBtm10">
            {t("adminPage.createdAtInfoText")}
          </div>

          <div>
            <AgTable
              columnDefs={colDefs}
              rowData={rowData}
              customHeight="55vh"
              rowHeight={50}
              onSortChanged={onSortChanged}
              hideExport
              context={{ handleReferenceRowClick: handleShowPlants }}
            />
            <DupontDialog
              open={dialogOpen}
              onClose={handleDialogClose}
              title={t("adminPage.plantDetails")}
              isOverlayLoader={showOverLayLoader}
              minWidth={900}
            >
              {
                <AgTable
                  columnDefs={plantColDefs}
                  rowData={plantRowData}
                  customHeight="55vh"
                  rowHeight={50}
                  hideExport
                  context={{
                    ...{ handleReferenceRowClick: handleShowPlants },
                    navigate,
                  }}
                  onRowClicked={handlePlantRowClicked}
                />
              }
            </DupontDialog>
            <div className="pagination-controls">
              <label htmlFor="pageSize" className="page-size-label">
                {t("adminPage.pageSize")}
              </label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={(e) => {
                  setCurrentPage(1);
                  setPageSize(Number(e.target.value));
                }}
                className="page-size-select"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <DupontIconButton
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="pagination-btn prev-btn"
                tooltip={t("adminPage.previous")}
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </DupontIconButton>
              <span className="pagination-info">
                {t("adminPage.pageInfo", {
                  current: currentPage,
                  total: totalPages,
                })}
              </span>
              <DupontIconButton
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages || totalPages === 0}
                className="pagination-btn next-btn"
                tooltip={t("adminPage.next")}
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </DupontIconButton>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default UserPage;
